import { alertController, modalController } from '@ionic/core';
import { Component, Element, Event, EventEmitter, Fragment, h, Prop, State } from '@stencil/core';
import { DrawingDoc, DrawingPageDoc, FileDoc, FileType, PersonDoc } from 'common';
import { debounce } from 'lodash';
import { User } from 'src/global/models';
import { FileService } from 'src/global/service/FileService';
import { PeopleService } from 'src/global/service/PeopleService';
import { DrawingPageSettingsModal } from '../concourse-drawings/drawing-page-settings-modal';
import { DrawingSettingsModal } from '../concourse-drawings/drawing-settings-modal';
import { DrawingVersionsModal } from '../concourse-drawings/drawing-versions-modal';
import { DocumentCacheModal } from './document-cache-modal';
import { documentsStoreState, setPeople, setSelectedPeopleIds } from './documents-store';
import { tr } from '@faker-js/faker/.';

interface ViewerWithZoomNav {
  zoomIn: () => Promise<void>;
  zoomOut: () => Promise<void>;
  nextPage: () => Promise<void>;
  prevPage: () => Promise<void>;
}

interface ViewerWithSearch {
  searchEnterClicked: () => Promise<void>;
}

@Component({
  tag: 'document-viewer-modal',
  styleUrl: '../../global/app.css',
  shadow: true,
})
export class DocumentViewerModal {
  static modalInstance: DocumentViewerModal;

  static async present(projectId: string, file: FileDoc, fullScreen: boolean = false, onDismiss: Function = null, notifyMarkup: Function = null): Promise<HTMLIonModalElement> {
    const modal = await modalController.create({
      component: 'document-viewer-modal',
      componentProps: { projectId, file, fullScreen, notifyMarkup },
      cssClass: fullScreen ? 'modal-full-view' : 'modal-size-90',
    });

    modal.addEventListener('dismiss', () => {
      modal.dismiss();
      if (onDismiss) onDismiss();
    });

    await modal.present();
    return modal;
  }

  @Element() el: HTMLElement;

  @Prop() projectId: string;
  @Prop() file: FileDoc;
  @Prop() fullScreen: boolean;
  @Prop() notifyMarkup: Function;

  @State() pageId: string;
  @State() searchOpen: boolean;
  @State() searchTerm: string;
  @State() filtersOpen: boolean;

  @Event() dismiss: EventEmitter;

  @State() drawingSet: DrawingDoc[];
  @State() drawingPages: DrawingPageDoc[];
  @State() markupToolsOpen: boolean = false;
  @State() allowDismiss: boolean = false;
  @State() pageInfo: { currentIndex: number; total: number } = { currentIndex: 0, total: 0 };

  get user(): User {
    return globalThis.user;
  }
  get isMobile(): boolean {
    return globalThis.isMobile;
  }

  get drawingDoc(): DrawingDoc {
    return this.file.type == FileType.DRAWING ? (this.file as DrawingDoc) : null;
  }

  get people(): PersonDoc[] {
    return documentsStoreState.people;
  }

  async componentWillLoad() {
    console.log('drawings-viewer componentWillLoad()');
    DocumentViewerModal.modalInstance = this;

    // Load people data for filtering
    setPeople(await PeopleService.getPersonDocs(this.projectId));

    if (this.file.type == FileType.SET) {
      const db = FileService.getFilesDb(this.projectId);
      const docs = await db.getDocsAwaitSync();
      this.drawingSet = docs.filter(d => d.parent_id == this.file._id);
    }

    this.markupToolsOpen = false;
  }

  componentDidRender() {
    this.syncScrapbookMarkupToolsOpen();
  }

  disconnectedCallback() {
    DocumentViewerModal.modalInstance = null;
  }

  onSearchInput = debounce((term: string) => {
    this.searchOpen = !!term && term.length > 2;
    this.searchTerm = term;
  }, 300);

  exitSearch() {
    this.searchOpen = false;
    //this.searchTerm = '';
  }

  changePageId(pageId: string) {
    this.pageId = pageId;
  }

  onPageInfoChange(e: CustomEvent<{ currentIndex: number; total: number }>) {
    const next = e.detail;
    if (this.pageInfo.currentIndex === next.currentIndex && this.pageInfo.total === next.total) return;
    queueMicrotask(() => {
      this.pageInfo = next;
    });
  }

  onSelectDrawingPages(pages: DrawingPageDoc[]) {
    this.pageId = pages?.length > 0 ? pages[0]._id : null;
    this.drawingPages = pages;
  }

  async openSetSettings() {
    console.log('editSelectedFolder: ');
    const alert = await alertController.create({
      header: `Drawing set name`,
      message: `Update the name of the drawing set`,
      inputs: [
        {
          name: 'setName',
          type: 'text',
          value: this.file.name,
          placeholder: `Enter drawing set name`,
        },
      ],
      buttons: [
        {
          text: 'Cancel',
          role: 'cancel',
        },
        {
          text: 'Update',
          handler: async data => {
            this.file = await FileService.saveFile(this.projectId, { ...this.file, name: data.setName });
          },
        },
      ],
    });
    await alert.present();
  }
  async openDrawingSettings() {
    DrawingSettingsModal.present(this.projectId, this.file as DrawingDoc, async (updated: DrawingDoc) => {
      this.file = await FileService.saveFile(this.projectId, updated);
    });
  }

  async openDrawingPageSettings() {
    const updated = await DrawingPageSettingsModal.present(this.projectId, this.drawingPages[0]);
    if (updated) {
      await FileService.savePage(this.projectId, updated);
    }
  }

  async openDrawingComparisonModal() {
    const updated = await DrawingVersionsModal.present(this.projectId, this.drawingSet, this.drawingPages);
    updated.forEach(async page => await FileService.savePage(this.projectId, page));
  }

  async onBackClick() {
    if ((this.file.type == FileType.DRAWING || this.file.type == FileType.SET) && this.drawingPages) {
      this.pageId = null;
      this.drawingPages = null;
    } else {
      this.dismiss.emit();
    }
  }

  private async downloadFile() {
    // check if file is a marked up file
    const isMarkupFile = this.file.type === FileType.SCRAPBOOK || this.file.type === FileType.DOCUMENT;

    if (isMarkupFile) {
      const canDownloadWithMarkups = await this.onDownloadScrapbookClick(this.file.s3_key);
      if (canDownloadWithMarkups) {
        return;
      }
    }

    if (this.isMobile) {
      DocumentCacheModal.present(this.projectId, this.file);
    } else {
      const url = await FileService.getSignedUrl(this.file.s3_key);
      window.open(url, '_blank');
    }
  }

  async onDownloadScrapbookClick(fileS3key?: string): Promise<boolean> {
    const scrapbookEditor = this.el.shadowRoot?.querySelector('scrapbook-editor') as HTMLScrapbookEditorElement;
    const documentViewer = this.el.shadowRoot?.querySelector('document-viewer-pages') as HTMLDocumentViewerPagesElement;
    const editor = scrapbookEditor || documentViewer;
    if (editor && editor?.savePdf) {
      await editor.savePdf(fileS3key);
      return true;
    }
    return false;
  }

  toggleMarkupsOnDrawing() {
    this.markupToolsOpen = !this.markupToolsOpen;
  }

  async findSubmittalsWithAI() {
    await this.specificationViewer.analyseDocumentWithAI();
  }

  onSearchEnterClicked() {
    const searchTerm = this.searchTerm;
    this.viewerWithAiSearch?.searchEnterClicked();

    // the header will clear the search term, so we need to restore it
    requestAnimationFrame(() => {
      this.searchOpen = true;
      this.searchTerm = searchTerm;
    });
  }

  private syncScrapbookMarkupToolsOpen() {
    if (this.file.type !== FileType.SCRAPBOOK) {
      return;
    }
    const editor = this.scrapbookEditor;
    if (editor) {
      editor.markupToolsOpen = this.markupToolsOpen;
    }
  }

  handleToggleFilters() {
    this.filtersOpen = !this.filtersOpen;
  }

  async handleNotifyMarkUpDone() {
    await this.notifyMarkup();
  }

  private get viewerWithAiSearch(): ViewerWithSearch {
    return this.specificationViewer;
  }

  private get viewerWithZoomNav(): ViewerWithZoomNav {
    return this.drawingsViewer || this.documentViewer || this.scrapbookEditor || this.specificationViewer;
  }

  get drawingsViewer(): HTMLDrawingsViewerElement {
    return this.el.shadowRoot?.querySelector('drawings-viewer') as HTMLDrawingsViewerElement;
  }

  get documentViewer(): HTMLDocumentViewerElement {
    return this.el.shadowRoot?.querySelector('document-viewer') as HTMLDocumentViewerElement;
  }

  get scrapbookEditor(): HTMLScrapbookEditorElement {
    return this.el.shadowRoot?.querySelector('scrapbook-editor') as HTMLScrapbookEditorElement;
  }

  get specificationViewer(): HTMLSpecificationViewerElement {
    return this.el.shadowRoot?.querySelector('specification-viewer') as HTMLSpecificationViewerElement;
  }

  private async handleZoomIn() {
    await this.viewerWithZoomNav?.zoomIn?.();
  }

  private async handleZoomOut() {
    await this.viewerWithZoomNav?.zoomOut?.();
  }

  private async handleNextPage() {
    await this.viewerWithZoomNav?.nextPage?.();
  }

  private async handlePrevPage() {
    await this.viewerWithZoomNav?.prevPage?.();
  }

  render() {
    const showBackButton = this.fullScreen || this.isMobile;
    const canSearch = [FileType.DOCUMENT, FileType.SPECIFICATION, FileType.DRAWING, FileType.SET].includes(this.file.type);
    const modalTitle = this.file['title'] || this.file.name;

    return (
      <Fragment>
        <concourse-header
          showBack={showBackButton}
          showSearchButton={canSearch}
          projectId={this.projectId}
          pageTitle={modalTitle || 'Document Viewer'}
          onBack={() => this.onBackClick()}
          searchValue={this.searchTerm}
          onSearchInput={e => this.onSearchInput(e.detail)}
          onEnterClicked={() => this.onSearchEnterClicked()}
        >
          <Fragment>
            <div class="toolbar-separator" />
            <ion-button shape="round" color="light" fill="clear" onClick={() => this.handleZoomIn()}>
              <ion-icon slot="icon-only" name="add-circle-outline"></ion-icon>
            </ion-button>
            <ion-button shape="round" color="light" fill="clear" onClick={() => this.handleZoomOut()}>
              <ion-icon slot="icon-only" name="remove-circle-outline"></ion-icon>
            </ion-button>
            <div class="toolbar-separator" />
            {this.pageInfo.total > 1 && (
              <div class="page-counter-controls ion-hide-sm-down" style={{ display: 'flex', alignItems: 'center', gap: '0', margin: '0 0.25rem' }}>
                <ion-button fill="clear" onClick={() => this.handlePrevPage()}>
                  <ion-icon slot="icon-only" name="chevron-back"></ion-icon>
                </ion-button>
                <span class="page-counter" style={{ whiteSpace: 'nowrap', margin: '0 0.25rem' }}>
                  {String(this.pageInfo.currentIndex + 1)}/{String(this.pageInfo.total)}
                </span>
                <ion-button fill="clear" onClick={() => this.handleNextPage()}>
                  <ion-icon slot="icon-only" name="chevron-forward"></ion-icon>
                </ion-button>
                <div class="toolbar-separator" />
              </div>
            )}

            {this.file.type == FileType.SET && !this.drawingPages && (
              <ion-button shape="round" color="light" fill="clear" onClick={() => this.openSetSettings()}>
                <ion-icon slot="icon-only" name="settings-outline"></ion-icon>
              </ion-button>
            )}
            {this.file.type == FileType.DRAWING && !this.drawingPages && (
              <ion-button shape="round" color="light" fill="clear" onClick={() => this.openDrawingSettings()}>
                <ion-icon slot="icon-only" name="settings-outline"></ion-icon>
              </ion-button>
            )}
            {this.drawingPages && this.drawingPages.length == 1 && (
              <ion-button id="drawing-page-settings-button" shape="round" color="light" fill="clear" onClick={() => this.openDrawingPageSettings()}>
                <ion-icon slot="icon-only" name="settings-outline"></ion-icon>
              </ion-button>
            )}
            {this.drawingPages && this.drawingPages.length > 1 && (
              <ion-button id="drawing-pages-compare" shape="round" color="light" fill="clear" onClick={() => this.openDrawingComparisonModal()}>
                <ion-icon slot="icon-only" src="/assets/icon/compare-icon.svg"></ion-icon>
              </ion-button>
            )}
            {(this.file.type === FileType.DRAWING || this.file.type === FileType.DOCUMENT || this.file.type === FileType.SCRAPBOOK) && (
              <ion-button shape="round" color="light" fill="clear" onClick={() => this.toggleMarkupsOnDrawing()}>
                <ion-icon slot="icon-only" class="header-icon" src="/assets/icon/pencil.svg" color={this.markupToolsOpen ? 'primary' : 'light'}></ion-icon>
              </ion-button>
            )}
            {this.file.type === FileType.SPECIFICATION && (
              <ion-button shape="round" color="light" fill="clear" onClick={() => this.findSubmittalsWithAI()}>
                <ion-icon slot="icon-only" src="/assets/icon/ai-icon.svg"></ion-icon>
              </ion-button>
            )}
            <ion-button shape="round" color="light" fill="clear" onClick={() => this.downloadFile()}>
              <ion-icon slot="icon-only" name="cloud-download-outline"></ion-icon>
            </ion-button>
          </Fragment>
        </concourse-header>
        {this.file.type == FileType.SPECIFICATION ? (
          <specification-viewer
            projectId={this.projectId}
            file={this.file}
            pageId={this.pageId}
            searchOpen={this.searchOpen}
            searchTerm={this.searchTerm}
            onCloseSearch={() => this.exitSearch()}
            onChangePageId={e => this.changePageId(e.detail)}
            onPageInfoChange={e => this.onPageInfoChange(e)}
            style={{ height: '100%' }}
          />
        ) : this.file.type == FileType.SCRAPBOOK ? (
          <scrapbook-editor projectId={this.projectId} scrapbookId={this.file._id} style={{ height: '100%' }} />
        ) : this.file.type == FileType.SET ? (
          <drawings-viewer
            projectId={this.projectId}
            files={this.drawingSet}
            fileId={this.file._id}
            pagesSelected={this.drawingPages}
            searchOpen={this.searchOpen}
            searchTerm={this.searchTerm}
            onCloseSearch={() => this.exitSearch()}
            onSelectPages={(e: CustomEvent) => this.onSelectDrawingPages(e.detail)}
            onPageInfoChange={e => this.onPageInfoChange(e)}
            style={{ height: '100%' }}
            showMarkupsOnDrawing={this.markupToolsOpen}
            hasMarkupToolbar={true}
          />
        ) : this.file.type == FileType.DRAWING ? (
          <drawings-viewer
            projectId={this.projectId}
            fileId={this.file._id}
            files={[this.file]}
            pagesSelected={this.drawingPages}
            searchOpen={this.searchOpen}
            searchTerm={this.searchTerm}
            onCloseSearch={() => this.exitSearch()}
            onSelectPages={(e: CustomEvent) => this.onSelectDrawingPages(e.detail)}
            onPageInfoChange={e => this.onPageInfoChange(e)}
            style={{ height: '100%' }}
            showMarkupsOnDrawing={this.markupToolsOpen}
            hasMarkupToolbar={true}
          />
        ) : (
          <document-viewer
            projectId={this.projectId}
            file={this.file}
            pageId={this.pageId}
            searchTerm={this.searchTerm}
            onCloseSearch={() => this.exitSearch()}
            onChangePageId={e => this.changePageId(e.detail)}
            onPageInfoChange={e => this.onPageInfoChange(e)}
            style={{ height: '100%' }}
            markupToolsOpen={this.markupToolsOpen}
            notifyMarkups={this.notifyMarkup ? () => this.handleNotifyMarkUpDone() : null}
            showFilters={this.filtersOpen}
            togglePeopleFilters={() => this.handleToggleFilters()}
            searchOpen={this.searchOpen}
          />
        )}
      </Fragment>
    );
  }
}
